import React from "react";
import { X, CheckCircle, AlertCircle } from "lucide-react";
import { Order } from "./types";

interface StageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (stage: "without-post-office" | "with-post-office" | "shipped") => void;
  ordersByStage: {
    "without-post-office": Order[];
    "with-post-office": Order[];
    shipped: Order[];
  };
}

const StageSelectionModal: React.FC<StageSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  ordersByStage,
}) => {
  const [selectedStage, setSelectedStage] = React.useState<
    "without-post-office" | "with-post-office" | "shipped" | null
  >(null);

  // Auto-select the first stage with orders
  React.useEffect(() => {
    if (ordersByStage["without-post-office"].length > 0) {
      setSelectedStage("without-post-office");
    } else if (ordersByStage["with-post-office"].length > 0) {
      setSelectedStage("with-post-office");
    } else if (ordersByStage.shipped.length > 0) {
      setSelectedStage("shipped");
    }
  }, [ordersByStage]);

  if (!isOpen) return null;

  const getStageLabel = (stage: string): string => {
    switch (stage) {
      case "without-post-office":
        return "Without Post Office";
      case "with-post-office":
        return "Post Office Selected";
      case "shipped":
        return "Shipped";
      default:
        return stage;
    }
  };

  const getStageDescription = (stage: string): string => {
    switch (stage) {
      case "without-post-office":
        return "Orders that need post office selection";
      case "with-post-office":
        return "Orders ready to ship";
      case "shipped":
        return "Orders already shipped";
      default:
        return "";
    }
  };

  const totalOrders = Object.values(ordersByStage).reduce(
    (sum, orders) => sum + orders.length,
    0
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
            Multiple Stages Detected
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 mb-4">
            You have selected {totalOrders} order{totalOrders !== 1 ? "s" : ""} across different stages. 
            Please choose which stage to keep. Orders from other stages will be unselected.
          </p>

          {/* Stage Options */}
          <div className="space-y-3">
            {ordersByStage["without-post-office"].length > 0 && (
              <div
                onClick={() => setSelectedStage("without-post-office")}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedStage === "without-post-office"
                    ? "border-[#264a3e] bg-green-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div
                      className={`mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                        selectedStage === "without-post-office"
                          ? "border-[#264a3e] bg-[#264a3e]"
                          : "border-gray-300"
                      }`}
                    >
                      {selectedStage === "without-post-office" && (
                        <CheckCircle className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {getStageLabel("without-post-office")}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {getStageDescription("without-post-office")}
                      </div>
                      <div className="text-xs text-gray-400 mt-2">
                        {ordersByStage["without-post-office"].length} order
                        {ordersByStage["without-post-office"].length !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {ordersByStage["with-post-office"].length > 0 && (
              <div
                onClick={() => setSelectedStage("with-post-office")}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedStage === "with-post-office"
                    ? "border-[#264a3e] bg-green-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div
                      className={`mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                        selectedStage === "with-post-office"
                          ? "border-[#264a3e] bg-[#264a3e]"
                          : "border-gray-300"
                      }`}
                    >
                      {selectedStage === "with-post-office" && (
                        <CheckCircle className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {getStageLabel("with-post-office")}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {getStageDescription("with-post-office")}
                      </div>
                      <div className="text-xs text-gray-400 mt-2">
                        {ordersByStage["with-post-office"].length} order
                        {ordersByStage["with-post-office"].length !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {ordersByStage.shipped.length > 0 && (
              <div
                onClick={() => setSelectedStage("shipped")}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedStage === "shipped"
                    ? "border-[#264a3e] bg-green-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div
                      className={`mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                        selectedStage === "shipped"
                          ? "border-[#264a3e] bg-[#264a3e]"
                          : "border-gray-300"
                      }`}
                    >
                      {selectedStage === "shipped" && (
                        <CheckCircle className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {getStageLabel("shipped")}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {getStageDescription("shipped")}
                      </div>
                      <div className="text-xs text-gray-400 mt-2">
                        {ordersByStage.shipped.length} order
                        {ordersByStage.shipped.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors"
          >
            Cancel Selection
          </button>
          <button
            onClick={() => selectedStage && onConfirm(selectedStage)}
            disabled={!selectedStage}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Keep {selectedStage ? getStageLabel(selectedStage) : "Stage"} Only
          </button>
        </div>
      </div>
    </div>
  );
};

export default StageSelectionModal;

